// frontend/src/components/auth/EmailVerificationPage.js
import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Mail } from 'lucide-react';
import { authAPI } from '../../services/api';
import toast from 'react-hot-toast';
import AuthLayout from './AuthLayout';

export const EmailVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const verificationAttempted = useRef(false);
  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (verificationAttempted.current) return;
      verificationAttempted.current = true;

      if (!token) {
        setStatus('no-token');
        return;
      }

      try {
        setStatus('loading');
        await authAPI.verifyEmail(token);
        setStatus('success');
        toast.success('Email verified successfully!');
      } catch (error) {
        const message = error.response?.data?.detail ||
                       error.message ||
                       'Failed to verify email. The link may be invalid or expired.';

        if (message.toLowerCase().includes('already') || message.toLowerCase().includes('used')) {
          setStatus('success');
          toast.success('Email verified successfully!');
          return;
        }

        setStatus('error');
        setErrorMessage(message);
        toast.error(message);
      }
    };

    verifyEmail();
  }, [token]);

  // Loading
  if (status === 'loading') {
    return (
      <AuthLayout title="Verifying your email">
        <div className="text-center py-4">
          <svg className="animate-spin h-12 w-12 text-accent mx-auto mb-4" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
          <p className="text-body text-ink-muted">Please wait while we verify your email address...</p>
        </div>
      </AuthLayout>
    );
  }

  // Success
  if (status === 'success') {
    return (
      <AuthLayout title="Email Verified">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-success-tint mb-4">
            <CheckCircle className="h-8 w-8 text-success" />
          </div>
          <p className="text-body text-ink-muted mb-6">
            Your email has been successfully verified. You can now access all features.
          </p>
          <div className="space-y-3">
            <Link
              to="/login"
              className="block w-full min-h-touch flex items-center justify-center rounded-lg
                bg-accent text-white font-medium text-body
                hover:bg-accent-hover transition-colors"
            >
              Log In to Your Account
            </Link>
            <Link
              to="/"
              className="block w-full min-h-touch flex items-center justify-center rounded-lg
                bg-white text-ink-soft border border-border-default font-medium text-body
                hover:bg-surface-muted transition-colors"
            >
              Go to Dashboard
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Error
  if (status === 'error') {
    return (
      <AuthLayout title="Verification Failed">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-danger-tint mb-4">
            <XCircle className="h-8 w-8 text-danger" />
          </div>
          <p className="text-body text-ink-muted mb-2">{errorMessage}</p>
          <p className="text-small text-ink-muted mb-6">
            This can happen if the link has expired or was already used.
          </p>
          <div className="space-y-3">
            <Link
              to="/login"
              className="block w-full min-h-touch flex items-center justify-center rounded-lg
                bg-accent text-white font-medium text-body
                hover:bg-accent-hover transition-colors"
            >
              Log In & Request New Link
            </Link>
            <Link
              to="/register"
              className="block w-full min-h-touch flex items-center justify-center rounded-lg
                bg-white text-ink-soft border border-border-default font-medium text-body
                hover:bg-surface-muted transition-colors"
            >
              Create New Account
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // No token
  return (
    <AuthLayout title="No Verification Token">
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-warning-tint mb-4">
          <Mail className="h-8 w-8 text-warning" />
        </div>
        <p className="text-body text-ink-muted mb-6">
          Please check your email for the verification link, or request a new one.
        </p>
        <div className="space-y-3">
          <Link
            to="/login"
            className="block w-full min-h-touch flex items-center justify-center rounded-lg
              bg-accent text-white font-medium text-body
              hover:bg-accent-hover transition-colors"
          >
            Log In to Your Account
          </Link>
          <Link
            to="/register"
            className="block w-full min-h-touch flex items-center justify-center rounded-lg
              bg-white text-ink-soft border border-border-default font-medium text-body
              hover:bg-surface-muted transition-colors"
          >
            Create New Account
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
};

export default EmailVerificationPage;
